/** Báo cáo giờ học — `S.report` của bản mẫu (spec 12): tab Hôm nay / Tuần này / 30 ngày. */
import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DaySummary, GoldenHour, type Account } from "@ca/shared";
import {
  Button,
  Card,
  CardHead,
  Chip,
  ErrorState,
  Kpi,
  Modal,
  PageHead,
  Skeleton,
  Tabs,
} from "@ca/ui";
import { api, loadMe } from "./api.ts";
import { uuidv7 } from "./queue.ts";
import { useToday } from "./productivity.tsx";
import { useFocus } from "./focus.tsx";
import { plusDay, useCalendar } from "./planner.tsx";
import { activityTotals, dayLabel, hourCols, useActivity, WEEKDAYS } from "./day-end.tsx";
import { NewBadge } from "./flags.tsx";
import {
  ACT,
  HBars,
  HeatLegend,
  Legend,
  StackedCols,
  fmtMin,
  fmtShort,
  heatClass,
  presentActs,
  sum,
  tipFor,
  type Col,
  type Parts,
} from "./charts.tsx";

type Tab = "day" | "week" | "month";
const SHORT_DAYS = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
const weekday = (date: string) => new Date(`${date}T00:00:00`).getDay();
const ddmm = (date: string) => `${date.slice(8, 10)}/${date.slice(5, 7)}`;

function RuleBox() {
  return (
    <div className="rulebox">
      <span className="ii">⏺</span>
      <div>
        <b>Giờ học chỉ tính khi bạn đã vào làm</b>
        <p>
          <b>Có tính:</b> trong đề, phiên luyện part, bài trị lỗi, phiên ôn thẻ, chép chính tả,
          luyện paraphrase, lúc đọc một chủ đề Lý thuyết ngữ pháp, lúc viết ghi chú hay đọc một bài
          blog. <b>Không tính:</b> bấm vào menu, lướt danh sách đề, mở hộp chọn chế độ, xem báo cáo,
          xem bảng điểm. <b>Tạm dừng:</b> khi mở màn tập trung toàn màn hình, sang tab khác, hoặc 3
          phút không thao tác.
        </p>
      </div>
    </div>
  );
}
function TableToggle({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div id={id} className="tbl-wrap">
      {children}
    </div>
  );
}
function ActTable({
  rows,
  first,
}: {
  rows: { label: string; parts: Parts; future?: boolean }[];
  first: string;
}) {
  return (
    <table className="tbl">
      <thead>
        <tr>
          <th>{first}</th>
          {ACT.map((a) => (
            <th key={a.id} className="n">
              {a.name}
            </th>
          ))}
          <th className="n">Tổng</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label}>
            <td>{r.label}</td>
            {ACT.map((a) => (
              <td key={a.id} className="n">
                {r.parts[a.id] ? Math.round(r.parts[a.id] ?? 0) : ""}
              </td>
            ))}
            <td className="n">
              <b>{r.future ? "—" : Math.round(sum(r.parts))}</b>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function EmptyChart() {
  return (
    <p className="faint" style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>
      Học vài phút để Cá vẽ biểu đồ.
    </p>
  );
}

export function ReportPage({ account, notify }: { account: Account; notify: (s: string) => void }) {
  const uid = account.id,
    q = useToday(uid),
    cache = useQueryClient();
  const [tab, setTab] = useState<Tab>("day"),
    [goalOpen, setGoalOpen] = useState(false),
    [goal, setGoal] = useState(account.profile.dailyGoalMinutes),
    [saving, setSaving] = useState(false);
  if (q.isPending) return <Skeleton />;
  if (q.isError)
    return (
      <ErrorState
        message={String(q.error)}
        onRetry={() => {
          void q.refetch();
        }}
      />
    );
  const t = q.data;
  const now = new Date();
  return (
    <>
      <PageHead
        space="prod"
        title={
          <>
            Báo cáo giờ học <NewBadge />
          </>
        }
        desc="Biết hôm nay, tuần này bạn đã học bao lâu, học lúc nào và học gì. Giờ học chỉ được tính từ lúc bạn vào làm một bài, không tính lúc bấm qua các mục."
      >
        <div className="row between">
          <Tabs
            label="Khoảng báo cáo"
            items={[
              { key: "day", label: "Hôm nay" },
              { key: "week", label: "Tuần này" },
              { key: "month", label: "30 ngày" },
            ]}
            active={tab}
            onChange={setTab}
          />
          <div className="row" style={{ gap: 6 }}>
            <Chip>
              {dayLabel(t.date).replace(/ (\d\d\/\d\d)$/, ", $1")} · lúc{" "}
              {`${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`}
            </Chip>
            <button
              className="chip acc"
              onClick={() => {
                setGoal(t.goalMinutes);
                setGoalOpen(true);
              }}
            >
              🎯 Mục tiêu {t.goalMinutes} phút/ngày · đổi
            </button>
          </div>
        </div>
      </PageHead>
      <RuleBox />
      {tab === "day" ? (
        <ReportDay uid={uid} date={t.date} goal={t.goalMinutes} activeSecs={t.activeSecs} />
      ) : tab === "week" ? (
        <ReportWeek uid={uid} date={t.date} goal={t.goalMinutes} />
      ) : (
        <ReportMonth uid={uid} date={t.date} streak={t.streak} />
      )}
      {goalOpen && (
        <Modal
          title="Mục tiêu phút mỗi ngày"
          onClose={() => {
            setGoalOpen(false);
          }}
        >
          <div className="stack">
            <p className="muted">
              Cá dùng con số này làm vòng mục tiêu mỗi ngày ở Hôm nay và Báo cáo.
            </p>
            <div className="field">
              <label htmlFor="goal-minutes">Phút/ngày</label>
              <input
                id="goal-minutes"
                className="input"
                aria-label="Phút mỗi ngày"
                type="number"
                min="5"
                max="480"
                value={goal}
                onChange={(e) => {
                  setGoal(Number(e.target.value));
                }}
              />
            </div>
            <div className="row">
              {[15, 30, 60, 90, 120].map((v) => (
                <button
                  key={v}
                  className={`chip${goal === v ? " dark" : ""}`}
                  style={{ height: 36, padding: "0 14px" }}
                  onClick={() => {
                    setGoal(v);
                  }}
                >
                  {v} phút
                </button>
              ))}
            </div>
            <div>
              <Button
                disabled={saving || !Number.isInteger(goal) || goal < 5 || goal > 480}
                onClick={() => {
                  setSaving(true);
                  void (async () => {
                    const me = await loadMe();
                    await api("/v1/me/activity/goal", {
                      method: "PATCH",
                      accountId: uid,
                      key: uuidv7(),
                      body: { minutes: goal, expectedRev: me.profile.rev },
                    });
                    await cache.invalidateQueries({ queryKey: ["me"] });
                    await q.refetch();
                    setGoalOpen(false);
                  })()
                    .catch((e: unknown) => {
                      notify(String(e));
                    })
                    .finally(() => {
                      setSaving(false);
                    });
                }}
              >
                Lưu mục tiêu
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function ReportDay({
  uid,
  date,
  goal,
  activeSecs,
}: {
  uid: string;
  date: string;
  goal: number;
  activeSecs: number;
}) {
  const report = useActivity(uid, date, date),
    focus = useFocus(uid),
    day = useQuery({
      queryKey: ["day-summary", uid, date],
      queryFn: async () =>
        DaySummary.parse(await api(`/v1/me/day-summaries/${date}`, { accountId: uid })),
      refetchInterval: 30000,
    }),
    golden = useQuery({
      queryKey: ["golden", uid],
      queryFn: async () => GoldenHour.parse(await api("/v1/me/golden-hour", { accountId: uid })),
    });
  const [table, setTable] = useState(false);
  const tm = Math.floor(activeSecs / 60),
    rounds = focus.data?.rounds.length ?? 0,
    answered = day.data?.answered ?? 0,
    acc = answered ? Math.round(((day.data?.correct ?? 0) / answered) * 100) : null,
    fixed = day.data?.fixedGroups ?? [];
  const cols = hourCols(report.data);
  const curH = new Date().getHours();
  for (const [k, c] of cols.entries()) c.strong = k + 6 === curH;
  const totals = activityTotals(report.data);
  const bands: [string, string, number, number][] = [
    ["Sáng", "6–11h", 6, 11],
    ["Trưa", "11–14h", 11, 14],
    ["Chiều", "14–18h", 14, 18],
    ["Tối", "18–21h", 18, 21],
    ["Khuya", "21–24h", 21, 24],
  ];
  const rates = bands.map(([name, span, from, to]) => {
    const hours = golden.data?.hours.filter((h) => h.hour >= from && h.hour < to) ?? [];
    const a = hours.reduce((n, h) => n + h.answered, 0),
      c = hours.reduce((n, h) => n + h.correct, 0);
    return { name, span, rate: a >= 10 ? Math.round((c / a) * 100) : null };
  });
  const best = rates.reduce<(typeof rates)[number] | null>(
    (b, r) => (r.rate !== null && (b?.rate ?? -1) < r.rate ? r : b),
    null,
  );
  return (
    <>
      <div className="kpis">
        <Kpi
          className={tm >= goal ? "good" : undefined}
          label="Tổng giờ học"
          value={`${tm} phút`}
          sub={
            tm >= goal
              ? `✓ vượt mục tiêu ${tm - goal} phút`
              : `mục tiêu ${goal} phút · còn ${goal - tm}`
          }
        />
        <Kpi
          label="Tập trung 🍅"
          value={`${rounds} hiệp`}
          sub={rounds ? `≈ ${rounds * 25} phút không xao nhãng` : "chưa bật hiệp nào"}
        />
        <Kpi
          label="Câu đã làm"
          value={answered}
          sub={acc !== null ? `đúng ${acc}%` : "chưa làm câu nào"}
        />
        <Kpi
          label="Từ đã ôn"
          value={day.data?.reviewedWords ?? 0}
          sub={`+${day.data?.newWords ?? 0} từ mới lưu`}
        />
        <Kpi
          label="Lỗi đã trị"
          value={`${fixed.length} nhóm`}
          sub={
            fixed.length ? [...new Set(fixed.map((g) => g.title))].join(", ") : "chưa trị nhóm nào"
          }
        />
      </div>
      <Card>
        <CardHead
          title="Bạn học lúc nào?"
          sub="Phút học theo từng giờ trong ngày, chia theo việc. Rê chuột hoặc bấm Tab vào cột để xem chi tiết."
        >
          <Button
            variant="line"
            size="sm"
            aria-expanded={table}
            onClick={() => {
              setTable(!table);
            }}
          >
            {table ? "Xem dạng biểu đồ" : "Xem dạng bảng"}
          </Button>
        </CardHead>
        <Legend ids={presentActs(cols.map((c) => c.parts))} />
        <div className="chart-scroll">
          <div className="chart">
            <StackedCols
              cols={cols}
              W={760}
              H={240}
              step={20}
              labelEvery={2}
              aria="Phút học theo 24 giờ"
            />
          </div>
        </div>
        {!tm && <EmptyChart />}
        {table && (
          <TableToggle id="tblHourly">
            <ActTable first="Giờ" rows={cols.filter((c) => sum(c.parts) > 0)} />
          </TableToggle>
        )}
      </Card>
      <div className="grid g-7-5">
        <Card>
          <CardHead
            title="Hôm nay học gì?"
            sub={`${fmtMin(tm)}, chia theo việc · tự ghi từ lúc bạn làm bài`}
          />
          {sum(totals) ? <HBars totals={totals} /> : <EmptyChart />}
        </Card>
        <Card className="stack">
          <div className="insight">
            <span className="ii">🌟</span>
            <div>
              {best ? (
                <>
                  <b>Giờ vàng của bạn: {best.span}</b>
                  <p>
                    Bạn làm đúng {best.rate}% câu khi học buổi {best.name.toLowerCase()}, cao nhất
                    trong các khung giờ. Cá ưu tiên xếp phần khó vào giờ này.
                  </p>
                </>
              ) : (
                <>
                  <b>Giờ vàng của bạn</b>
                  <p>Cần ít nhất 10 câu được chấm trong một khung giờ để gợi ý.</p>
                </>
              )}
            </div>
          </div>
          <div className="eyebrow">Tỉ lệ làm đúng theo khung giờ</div>
          {rates.map((b) => (
            <div
              key={b.name}
              className="hbar"
              style={{ gridTemplateColumns: "96px minmax(0,1fr) 54px" }}
              data-tip={`${b.name} (${b.span})|${b.rate === null ? "Chưa có dữ liệu" : `Đúng ${b.rate}% số câu`}`}
            >
              <span className="hl">
                {b.name} <small className="faint">{b.span}</small>
              </span>
              <span className="ht">
                <i className={b === best ? "b1" : "b0"} style={{ width: `${b.rate ?? 0}%` }} />
              </span>
              <span className="hv">{b.rate === null ? "—" : `${b.rate}%`}</span>
            </div>
          ))}
          <p className="faint" style={{ fontSize: 12, fontWeight: 600 }}>
            Chỉ tính được khi một nền tảng có cả giờ học lẫn kết quả làm bài.
          </p>
        </Card>
      </div>
    </>
  );
}

function ReportWeek({ uid, date, goal }: { uid: string; date: string; goal: number }) {
  const monday = plusDay(date, -((weekday(date) + 6) % 7)),
    sunday = plusDay(monday, 6);
  const report = useActivity(uid, monday, date),
    plan = useCalendar(uid, monday, sunday);
  const [table, setTable] = useState(false);
  const week = Array.from({ length: 7 }, (_, i) => plusDay(monday, i));
  const byDay: Record<string, Parts> = {};
  for (const b of report.data?.buckets ?? []) {
    const d = (byDay[b.date] ??= {});
    d[b.activity] = (d[b.activity] ?? 0) + b.activeSecs / 60;
  }
  const planned: Record<string, number> = {};
  for (const e of plan.data ?? []) planned[e.date] = (planned[e.date] ?? 0) + e.endMin - e.startMin;
  const days = week.map((d) => (d > date ? {} : (byDay[d] ?? {})));
  const totals = days.map(sum);
  const cols: Col[] = week.map((d, i) => {
    const label = `${SHORT_DAYS[weekday(d)] ?? ""} ${ddmm(d)}`;
    return {
      label,
      parts: days[i] ?? {},
      future: d > date,
      strong: d === date,
      tip:
        d > date
          ? `${label}|Chưa tới · kế hoạch ${planned[d] ?? 0} phút`
          : tipFor(label, days[i] ?? {}),
    };
  });
  const passed = week.filter((d) => d <= date).length;
  const best = totals.indexOf(Math.max(...totals));
  const ti = week.indexOf(date);
  const bestCol = cols[best],
    todayCol = cols[ti];
  if (bestCol && (totals[best] ?? 0) > 0) bestCol.val = fmtShort(totals[best] ?? 0);
  if (todayCol && (totals[ti] ?? 0) > 0) todayCol.val = fmtShort(totals[ti] ?? 0);
  const wkTot = totals.reduce((a, b) => a + b, 0);
  const wkRounds = (report.data?.days ?? []).reduce((n, d) => n + d.focusRounds, 0),
    wkFocusMin = (report.data?.days ?? []).reduce((n, d) => n + d.focusSecs, 0) / 60;
  const agg: Parts = {};
  for (const p of days) for (const a of ACT) agg[a.id] = (agg[a.id] ?? 0) + (p[a.id] ?? 0);
  const weekGoal = goal * 7;
  const mx = Math.max(...week.map((d, i) => Math.max(planned[d] ?? 0, totals[i] ?? 0)), 60);
  return (
    <>
      <div className="kpis">
        <Kpi
          label="Tổng tuần này"
          value={fmtShort(wkTot)}
          sub={`mục tiêu ${fmtShort(weekGoal)} · còn ${fmtShort(Math.max(0, weekGoal - wkTot))}`}
        />
        <Kpi
          label="Trung bình mỗi ngày"
          value={`${Math.round(wkTot / passed)} phút`}
          sub={`${passed} ngày đã qua`}
        />
        <Kpi
          label="Ngày học nhiều nhất"
          value={wkTot ? (WEEKDAYS[weekday(week[best] ?? date)] ?? "") : "—"}
          sub={wkTot ? fmtMin(totals[best] ?? 0) : "chưa có phút học"}
        />
        <Kpi
          label="Hiệp tập trung"
          value={`${wkRounds} 🍅`}
          sub={wkRounds ? `≈ ${fmtShort(wkFocusMin)} không xao nhãng` : "chưa bật hiệp nào"}
        />
      </div>
      <Card>
        <CardHead
          title={`Tuần này · ${ddmm(monday)} – ${ddmm(sunday)}/${sunday.slice(0, 4)}`}
          sub={`Phút học mỗi ngày, chia theo việc · đường ngang là mục tiêu ${goal} phút/ngày`}
        >
          <Button
            variant="line"
            size="sm"
            onClick={() => {
              setTable(!table);
            }}
          >
            {table ? "Xem dạng biểu đồ" : "Xem dạng bảng"}
          </Button>
        </CardHead>
        <Legend ids={presentActs(days)} />
        <div className="chart-scroll">
          <div className="chart">
            <StackedCols
              cols={cols}
              W={760}
              H={250}
              step={30}
              goal={goal}
              goalLbl={`Mục tiêu ${goal} phút`}
              aria="Phút học mỗi ngày trong tuần"
            />
          </div>
        </div>
        {!wkTot && <EmptyChart />}
        {table && (
          <TableToggle id="tblWeek">
            <ActTable first="Ngày" rows={cols} />
          </TableToggle>
        )}
      </Card>
      <div className="grid g2">
        <Card>
          <CardHead
            title={
              <>
                Kế hoạch so với thực tế <NewBadge />
              </>
            }
            sub="Lịch học tuần (kế hoạch) đặt cạnh giờ tự ghi (thực tế)"
          />
          <PlanVsReal week={week} date={date} planned={planned} totals={totals} mx={mx} />
        </Card>
        <Card>
          <CardHead title="Tuần này học gì?" sub={`${fmtMin(wkTot)} chia theo việc`} />
          {wkTot ? <HBars totals={agg} /> : <EmptyChart />}
        </Card>
      </div>
    </>
  );
}
export function PlanVsReal({
  week,
  date,
  planned,
  totals,
  mx,
}: {
  week: string[];
  date: string;
  planned: Record<string, number>;
  totals: number[];
  mx: number;
}) {
  return (
    <>
      <div className="legend">
        <span>
          <i style={{ background: "var(--surface-3)" }} />
          Kế hoạch (từ Lịch)
        </span>
        <span>
          <i className="b1" />
          Thực tế (tự ghi)
        </span>
      </div>
      {week.map((d, i) => {
        const plan = planned[d] ?? 0,
          real = d > date ? null : Math.round(totals[i] ?? 0),
          today = d === date,
          name = SHORT_DAYS[weekday(d)] ?? "";
        return (
          <div
            key={d}
            className="hbar"
            style={{ gridTemplateColumns: "62px minmax(0,1fr) 84px", padding: "5px 0" }}
            data-tip={`${name} ${ddmm(d)}|Kế hoạch: ${plan} phút|${real === null ? "Chưa tới" : `Thực tế: ${real} phút`}`}
          >
            <span className="hl" style={today ? undefined : { fontWeight: 650 }}>
              {name} {today ? "· nay" : ""}
            </span>
            <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span className="ht" style={{ height: 7 }}>
                <i style={{ width: `${(plan / mx) * 100}%`, background: "var(--surface-3)" }} />
              </span>
              <span className="ht" style={{ height: 7 }}>
                <i className="b1" style={{ width: `${((real ?? 0) / mx) * 100}%` }} />
              </span>
            </span>
            <span className="hv">
              {real === null ? (
                <small>{plan ? `${plan}p dự kiến` : "trống"}</small>
              ) : (
                `${real}/${plan}p ${plan && real >= plan ? "✓" : ""}`
              )}
            </span>
          </div>
        );
      })}
    </>
  );
}

function ReportMonth({ uid, date, streak }: { uid: string; date: string; streak: number }) {
  const from = plusDay(date, -29);
  const report = useActivity(uid, from, date);
  const minutes: Record<string, number> = {};
  for (const d of report.data?.days ?? []) minutes[d.date] = d.activeSecs / 60;
  const days = Array.from({ length: 30 }, (_, i) => plusDay(from, i));
  const values = days.map((d) => minutes[d] ?? 0);
  const studied = values.filter((v) => v > 0).length,
    missed = values.filter((v, i) => v <= 0 && days[i] !== date).length,
    total = values.reduce((a, b) => a + b, 0);
  let longest = 0,
    run = 0,
    longestEnd = "";
  values.forEach((v, i) => {
    run = v > 0 ? run + 1 : 0;
    if (run > longest) {
      longest = run;
      longestEnd = days[i] ?? "";
    }
  });
  const lead = (weekday(from) + 6) % 7,
    trail = (7 - ((lead + 30) % 7)) % 7;
  return (
    <>
      <div className="kpis">
        <Kpi label="Ngày có học" value={`${studied}/30`} sub={`${ddmm(from)} → hôm nay`} />
        <Kpi
          label="Ngày bỏ lỡ"
          value={missed}
          sub={missed ? "ô có chấm đỏ" : "chưa bỏ lỡ ngày nào"}
        />
        <Kpi
          label="Chuỗi hiện tại"
          value={`${streak} ngày 🔥`}
          sub={
            longest
              ? `dài nhất: ${longest} ngày (${ddmm(plusDay(longestEnd, 1 - longest))}–${ddmm(longestEnd)})`
              : "chưa có chuỗi"
          }
        />
        <Kpi
          label="Tổng thời gian"
          value={fmtShort(total)}
          sub={`≈ ${Math.round(total / 30)} phút mỗi ngày`}
        />
      </div>
      <Card>
        <CardHead title="30 ngày gần nhất" sub="Ô càng đậm là học càng lâu, chấm đỏ là ngày bỏ lỡ.">
          <HeatLegend />
        </CardHead>
        <div style={{ maxWidth: 560 }}>
          <div className="heat">
            {["T2", "T3", "T4", "T5", "T6", "T7", "CN"].map((d) => (
              <div key={d} className="hd">
                {d}
              </div>
            ))}
            {Array.from({ length: lead }, (_, i) => (
              <div key={`l${i}`} />
            ))}
            {days.map((d, i) => {
              const v = values[i] ?? 0,
                today = d === date;
              return (
                <div
                  key={d}
                  className={`hc ${today && !v ? "h0" : heatClass(v)}${today ? " t" : ""}`}
                  tabIndex={0}
                  data-tip={`${ddmm(d)}${today ? " · hôm nay" : ""}|${v ? fmtMin(v) : today ? "Chưa học" : "Bỏ lỡ"}`}
                >
                  {Number(d.slice(8, 10))}
                </div>
              );
            })}
            {Array.from({ length: trail }, (_, i) => (
              <div key={`t${i}`} className="hc fut">
                {Number(plusDay(date, i + 1).slice(8, 10))}
              </div>
            ))}
          </div>
        </div>
      </Card>
    </>
  );
}
